import "./styles.scss";

import { useTranslation } from "react-i18next";

import { Reorder } from "framer-motion";

import { Text } from "@fragments";
import { IconGripVertical, IconPhoto } from "@tabler/icons-react";

// A single draggable row: grip · banner thumb · title · position. The whole row
// is the drag target; text selection is disabled so the gesture is always a drag.
function ReorderItem({ item, index }) {
    return (
        <Reorder.Item
            value={item}
            className="reorder-item"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(index * 0.03, 0.24) }}
            whileDrag={{
                scale: 1.015,
                boxShadow: "0 12px 28px -12px rgba(0, 0, 0, 0.55)",
            }}
        >
            <div className="reorder-item-handle">
                <IconGripVertical size={18} />
            </div>

            <div className="reorder-item-thumb">
                {item.banner ? (
                    <img src={item.banner} alt="" draggable={false} />
                ) : (
                    <div className="reorder-item-thumb--empty">
                        <IconPhoto size={18} />
                    </div>
                )}
            </div>

            <Text.h5
                className="reorder-item-title"
                value={item.name}
                maxLines={1}
                grow
            />

            <Text.span
                className="reorder-item-index"
                contrast={6}
                value={String(index + 1)}
            />
        </Reorder.Item>
    );
}

/**
 * Presentational drag-to-reorder list shared by the playlist and content
 * reorder modals. Owns only the UI; fetching and persistence live in each modal.
 *
 * @param {string} subtitle - Helper copy shown above the list.
 * @param {boolean} loading - Renders the loading state instead of the list.
 * @param {Array<{id:number,name:string,banner?:string}>} items - Ordered rows.
 * @param {(next:Array)=>void} onReorder - Receives the reordered array.
 * @param {string} emptyLabel - Copy shown when there are no items.
 */
export default function ReorderPanel({ subtitle, loading, items, onReorder, emptyLabel }) {
    const { t } = useTranslation("contentModals");

    return (
        <div className="reorder-panel">
            {subtitle && (
                <Text.span
                    className="reorder-panel-subtitle"
                    contrast={5}
                    value={subtitle}
                />
            )}

            {loading ? (
                <div className="reorder-panel-state">
                    <Text.span contrast={5} value={t("common:actions.loading")} />
                </div>
            ) : items.length === 0 ? (
                <div className="reorder-panel-state">
                    <Text.span contrast={5} value={emptyLabel} />
                </div>
            ) : (
                <Reorder.Group
                    as="div"
                    axis="y"
                    values={items}
                    onReorder={onReorder}
                    className="reorder-list"
                >
                    {items.map((item, index) => (
                        <ReorderItem key={item.id} item={item} index={index} />
                    ))}
                </Reorder.Group>
            )}
        </div>
    );
}
